/**
 * Pure row operations for the streaming and subscription shapes.
 *
 * These are deliberately pure functions over arrays rather than methods on a
 * component: they're the part every future consumer needs — `ry-chat` gluing
 * tokens, a log viewer appending lines, a live table patching a row — and
 * putting them here means those components inherit tested behavior instead of
 * each writing their own subtly different version.
 *
 * No DOM, no imports beyond the contract's types.
 */

import type { RyChunk, RyPatch, RyRecord } from './ry-data.js';

/**
 * Apply one streamed chunk, returning a NEW array.
 *
 * `append` extends a field on the LAST row — that's the LLM case, and doing it
 * here is what stops every chat implementation from reinventing token merging.
 * Appending with no rows yet creates the first one, so a stream that opens with
 * a delta doesn't drop it on the floor.
 */
export function applyChunk(
  rows: ReadonlyArray<RyRecord>,
  chunk: RyChunk,
  defaultField = 'text'
): ReadonlyArray<RyRecord> {
  switch (chunk.op) {
    case 'add':
      return chunk.row ? [...rows, chunk.row] : rows;

    case 'append': {
      const field = chunk.field ?? defaultField;
      const delta = chunk.delta ?? '';
      if (!delta) return rows;
      if (rows.length === 0) return [{ [field]: delta }];
      const next = rows.slice();
      const last = next[next.length - 1] as RyRecord;
      const current = last[field];
      next[next.length - 1] = {
        ...last,
        [field]: (typeof current === 'string' ? current : '') + delta,
      };
      return next;
    }

    case 'done':
      return rows;
  }
}

/**
 * Apply one subscription patch, matched on `id`, returning a NEW array.
 *
 * Matching on `id` rather than index is what keeps a push from disturbing
 * anything the user is doing. A patch for a row that isn't present is a no-op
 * for `update`/`delete` — the server may be describing a record outside the
 * window this widget is showing, which is normal, not an error.
 */
export function applyPatch(
  rows: ReadonlyArray<RyRecord>,
  patch: RyPatch
): ReadonlyArray<RyRecord> {
  const id = patch.row['id'];
  const index = id === undefined ? -1 : rows.findIndex((r) => r['id'] === id);

  switch (patch.type) {
    case 'insert':
      // Re-inserting a known id is an update, not a duplicate. Backends resend.
      if (index >= 0) {
        const next = rows.slice();
        next[index] = patch.row;
        return next;
      }
      return [...rows, patch.row];

    case 'update': {
      if (index < 0) return rows;
      const next = rows.slice();
      next[index] = { ...(rows[index] as RyRecord), ...patch.row };
      return next;
    }

    case 'delete':
      return index < 0 ? rows : rows.filter((_, i) => i !== index);
  }
}

/** Which shape a provider returned. Discrimination needs no flags. */
export function sourceShape(result: unknown): 'read' | 'stream' | 'subscribe' {
  if (result && typeof result === 'object') {
    if (Symbol.asyncIterator in (result as object)) return 'stream';
    if (typeof (result as { unsubscribe?: unknown }).unsubscribe === 'function') return 'subscribe';
  }
  return 'read';
}

/**
 * Which pagination mode a response implies.
 *
 * Both are first-class, and a widget shouldn't have to be told which it's
 * dealing with — it can see. A response carrying `total` can be paged by
 * number and sized on a scrollbar; one carrying `cursor` can only move
 * forward. `none` means the source returned everything it has.
 */
export function paginationMode(response: {
  total?: number;
  cursor?: string;
}): 'offset' | 'cursor' | 'none' {
  if (response.cursor !== undefined && response.cursor !== null) return 'cursor';
  if (typeof response.total === 'number') return 'offset';
  return 'none';
}
